import { Product, ProductSlug, ProductType } from "@/api/product-api";
import { CurrencyIsoCode } from "@/enums";
import { EssentialGameItem } from "@/models/orders/OrderModelV2";
import { CartService } from "@/services/cart-service";
import { CartItem, CartItemOption } from "@/store/cart-store/type";
import { ProductUtil } from "@/utils/ProductUtil";
import { produce } from "immer";

const MAX_QUANTITY = 4;

/** 전체 상품의 SKU → displayAmount 맵 생성 (같은 product 내 SKU는 동일 가격, US는 usPrice 적용) */
function buildSkuPriceMap(
  products: Product[],
  currency: CurrencyIsoCode,
  countryCode: string | null,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const product of products) {
    // ⚠️ 반드시 getProductPrice 경유 — 내부 getCountryAwarePrices 가 US usPrice 티어를 적용한다
    // (docs/pricing-tiers.md). opt.prices 에서 currency 로 직접 찾으면 US 사용자에게
    // 非-US 가격이 노출된다. PDP 브랜치(5월)가 usPrice 도입 이전 코드라 그렇게 돼 있었음.
    const price = ProductUtil.getProductPrice({
      currency,
      countryCode,
      product,
    });
    if (!price) continue;
    for (const opt of product.skuOptions ?? []) {
      map.set(opt.sku, price.displayAmount);
    }
  }
  return map;
}

/** 전체 상품의 SKU → { slug, options } 맵 생성 */
function buildSkuInfoMap(
  products: Product[],
): Map<string, { slug: ProductSlug; options: CartItemOption[] }> {
  const map = new Map<
    string,
    { slug: ProductSlug; options: CartItemOption[] }
  >();
  for (const product of products) {
    for (const opt of product.skuOptions ?? []) {
      map.set(opt.sku, {
        slug: product.slug,
        options: ProductUtil.getOptionsFromSkuOption(opt),
      });
    }
  }
  return map;
}

/** 카트에 있는 번들 아이템을 구성품 SKU로 분해 */
function expandBundlesToComponents(
  items: CartItem[],
  bundleProducts: Product[],
  skuInfoMap: Map<string, { slug: ProductSlug; options: CartItemOption[] }>,
): CartItem[] {
  const bundleComponentMap = new Map<string, string[]>();
  for (const bundle of bundleProducts) {
    for (const opt of bundle.skuOptions ?? []) {
      if (opt.components.length > 0 && opt.gameResells.length === 0) {
        bundleComponentMap.set(opt.sku, opt.components);
      }
    }
  }

  const result: CartItem[] = [];
  for (const item of items) {
    const components = bundleComponentMap.get(item.sku);
    if (components) {
      // 구성품 전부 skuInfoMap에 있어야 분해 가능
      const canExpand = components.every((sku) => skuInfoMap.has(sku));
      if (canExpand) {
        for (const componentSku of components) {
          const info = skuInfoMap.get(componentSku)!;
          const existing = result.find((r) => r.sku === componentSku);
          if (existing) {
            existing.quantity += item.quantity;
          } else {
            result.push({
              sku: componentSku,
              slug: info.slug,
              quantity: item.quantity,
              options: info.options,
            });
          }
        }
      } else {
        // 분해할 수 없는 번들은 원본 유지
        result.push({ ...item });
      }
    } else {
      const existing = result.find((r) => r.sku === item.sku);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        result.push({ ...item });
      }
    }
  }
  return result;
}

/**
 * 병합 판정에 필요한 카트/카탈로그 파생값. **candidate 와 무관**하므로 한 번 만들어
 * 여러 candidate 에 재사용한다(추천 제품 N개 × 카탈로그 순회를 N배로 만들지 않기 위함).
 */
export interface BundleMergeContext {
  skuPriceMap: Map<string, number>;
  bundleProducts: Product[];
  /** 기존 번들을 구성품으로 분해한 카트 */
  expandedItems: CartItem[];
  /** 분해된 카트의 sku→수량 (candidate 미포함) */
  basePool: Map<string, number>;
}

export function buildBundleMergeContext({
  items,
  products,
  currency,
  countryCode,
}: {
  items: CartItem[];
  products: Product[];
  currency: CurrencyIsoCode;
  countryCode: string | null;
}): BundleMergeContext {
  const skuPriceMap = buildSkuPriceMap(products, currency, countryCode);
  const skuInfoMap = buildSkuInfoMap(products);
  // ⚠️ `isUnlistedBundle` 을 반드시 함께 걸 것 — ProductUtil.getBundles(노출 목록)와
  // 같은 기준이어야 한다. 안 걸면 의도적으로 숨긴 번들(tactglove-dk2/dk3-bundle)이
  // 병합 후보로 살아남아, 아무도 고를 수 없는 제품이 카트에 나타난다.
  // dk2-bundle 은 실측상 usPrice=0 이라 아래 "가장 싼 번들" 경쟁까지 이긴다.
  const bundleProducts = products.filter(
    (p) =>
      p.productType === ProductType.BUNDLE &&
      !ProductUtil.isUnlistedBundle(p.slug),
  );

  const expandedItems = expandBundlesToComponents(
    items,
    bundleProducts,
    skuInfoMap,
  );

  const basePool = new Map<string, number>();
  for (const item of expandedItems) {
    basePool.set(item.sku, (basePool.get(item.sku) ?? 0) + item.quantity);
  }

  return { skuPriceMap, bundleProducts, expandedItems, basePool };
}

/** `findBundleUpgrade` 의 판정 결과 — 카트 재작성 없이 "무엇이 얼마에 만들어지나"만 담는다. */
export interface BundleUpgrade {
  bundle: Product;
  bundleSku: string;
  bundleOptions: CartItemOption[];
  components: string[];
  /** candidate 가 기여해서 **새로** 만들어지는 번들 수량 */
  additionalQuantity: number;
  bundlePrice: number;
  /** 구성품 개별가 합계. 없으면 undefined(구성품 중 하나라도 사용자 통화 가격 없음). */
  componentTotal?: number;
}

/**
 * candidate 를 카트에 넣으면 **어떤 번들이 실제로 만들어지는가** — 병합의 판정 단계.
 *
 * `makeNonGameBundles`(카트 재작성)와 번들 업그레이드 배지(표시)가 **이 함수를 공유**한다.
 * 표시가 자체 판정을 갖고 있으면 배지는 A 번들을 약속하는데 카트는 B 를 만들거나 아무것도
 * 안 만드는 divergence 가 난다(#5091 이 고친 클래스와 동일). 특히 조용한 축이 **수량**이다 —
 * "그 SKU 가 카트에 있나"만 보면, 구성품이 이미 기존 번들에 소진돼 `additionalQuantity <= 0`
 * 인 경우를 놓친다.
 *
 * 순수 함수다 — ctx 를 변형하지 않는다(호출처가 작업용 pool 을 따로 만든다).
 */
export function findBundleUpgrade(
  ctx: BundleMergeContext,
  candidate: { quantity: number; sku: string },
): BundleUpgrade | null {
  const { skuPriceMap, bundleProducts, expandedItems, basePool } = ctx;
  const candidateSku = candidate.sku;

  const pool = new Map(basePool);
  pool.set(candidateSku, (pool.get(candidateSku) ?? 0) + candidate.quantity);

  type Candidate = BundleUpgrade & { bundleSlug: ProductSlug };
  const candidates: Candidate[] = [];

  for (const bundle of bundleProducts) {
    for (const skuOption of bundle.skuOptions ?? []) {
      if (skuOption.gameResells.length > 0) continue;
      if (!skuOption.components.includes(candidateSku)) continue;

      // candidate 포함 시 만들 수 있는 번들 수량
      // ⚠️ 여기서 MAX_QUANTITY 로 클램프하면 안 된다 — full/base 가 둘 다 4 에서
      // 포화되면 candidate 의 순증분이 항상 0 으로 계산돼 병합이 통째로 무산된다.
      // (#5174 — 카트에 FPS Bundle 4개 + Pro 4개가 있으면 Buy Together & Save 가
      //  단품 폴백으로 떨어져 화면이 약속한 할인이 반영되지 않았다.)
      const withQtys = skuOption.components.map((sku) => pool.get(sku) ?? 0);
      if (!withQtys.every((q) => q > 0)) continue;
      const fullQuantity = Math.min(...withQtys);

      // candidate 없이 만들 수 있는 번들 수량
      const withoutQtys = skuOption.components.map(
        (sku) => expandedItems.find((i) => i.sku === sku)?.quantity ?? 0,
      );
      const baseQuantity = withoutQtys.every((q) => q > 0)
        ? Math.min(...withoutQtys)
        : 0;

      // 라인당 최대 수량 제한은 "실제로 생성할 번들 수"에만 적용한다.
      const additionalQuantity = Math.min(
        fullQuantity - baseQuantity,
        MAX_QUANTITY,
      );
      if (additionalQuantity <= 0) continue;

      // ⚠️ `?? 0` 금지 — buildSkuPriceMap 은 `ProductUtil.getPrice` 가 null 을 준 제품을
      // 아예 담지 않는다(`if (!price) continue`). null 이 되는 조건은 **사용자 통화에
      // 해당하는 가격 행이 없거나 정가(`originalAmount`)가 0 일 때**다(예: 신규 번들에 JPY 행
      // 누락 → JP 사용자 · mgmt 가 미등록에 채우는 0 — #5886 이후 0 도 미등록).
      // ⇒ **구성품**(예: 번들 배지)의 정가가 0 이면 이 번들은 절감률(`componentTotal`)이 숨겨지고
      //    아래 장바구니 병합 후보에서도 빠진다. 구성품도 가격 등록이 필요하다는 뜻이다.
      // 0 으로 대체하면 "가격 모름"이 "공짜"가 되어 아래 cheapest reduce 를 **무조건
      // 이긴다** → 그 통화로 팔 수도 없는 번들이 카트를 덮어쓰고, 가격 없는 라인(ghost)이
      // 되어 결제까지 샌다. 가격을 모르면 후보에서 제외하는 게 유일하게 안전한 선택이다.
      //
      // 참고: 정가 0 은 #5886 부터 `getPrice` 가 null 로 거른다(2026-10-01 이전엔 맵에 0 으로
      // 들어가 여기서 못 걸렀다 — dk2-bundle 이 실제로 usPrice=0 이었고 `isUnlistedBundle` 이 막았다).
      const bundlePrice = skuPriceMap.get(skuOption.sku);
      if (bundlePrice === undefined) continue;

      // 구성품 개별가 합계 — 배지의 "Save N%" 는 이 값 대비여야 정직하다(번들 자체의
      // MSRP 마크다운이 아니라 "업그레이드해서 얼마 아끼나"). 하나라도 가격이 없으면
      // undefined 로 두고 표시 쪽에서 절감률을 감춘다(0 으로 대체하면 절감률이 뻥튀김).
      const componentPrices = skuOption.components.map((sku) =>
        skuPriceMap.get(sku),
      );
      const componentTotal = componentPrices.some((p) => p === undefined)
        ? undefined
        : (componentPrices as number[]).reduce((sum, p) => sum + p, 0);

      candidates.push({
        bundle,
        bundleSlug: bundle.slug,
        bundleSku: skuOption.sku,
        bundleOptions: ProductUtil.getOptionsFromSkuOption(skuOption),
        components: skuOption.components,
        additionalQuantity,
        bundlePrice,
        componentTotal,
      });
    }
  }

  if (candidates.length === 0) return null;

  // 2개 이상이면 가장 저렴한 번들, 1개면 그대로
  return candidates.length >= 2
    ? candidates.reduce((a, b) => (a.bundlePrice < b.bundlePrice ? a : b))
    : candidates[0];
}

export class BundleMerger {
  /**
   * candidate를 포함하는 번들 중 최적 조합을 찾아 병합합니다.
   *
   * 1. 기존 번들을 구성품으로 분해
   * 2. candidate가 추가 생성을 가능하게 하는 번들을 탐색  ← `findBundleUpgrade`
   *    - 후보가 2개 이상이면 가장 저렴한 번들 선택
   *    - candidate가 기여하는 순증분만큼만 생성
   * 3. 남은 구성품으로 할인 큰 순서로 재번들링
   *
   * 1~2 단계는 표시 경로(번들 업그레이드 배지)와 공유한다 — `findBundleUpgrade` 주석 참고.
   * 이 함수는 그 판정 위에 **라인아이템 재작성**만 얹는다.
   */
  static makeNonGameBundles({
    items,
    candidate,
    products,
    currency,
    countryCode,
  }: {
    items: CartItem[];
    candidate: {
      slug: ProductSlug;
      quantity: number;
      options?: CartItemOption[];
      sku: string;
    };
    products: Product[];
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }): { items: CartItem[]; combinedResult: ProductSlug } | null {
    const candidateSku = candidate.sku;
    const ctx = buildBundleMergeContext({
      items,
      products,
      currency,
      countryCode,
    });
    const { skuPriceMap, bundleProducts, expandedItems, basePool } = ctx;

    // 1~2 단계(분해·후보 수집·최저가 선택) — 표시 경로와 공유하는 판정.
    const chosen = findBundleUpgrade(ctx, candidate);
    if (!chosen) return null;

    // 3 단계용 작업 pool. ctx.basePool 은 candidate 미포함 + 불변이므로 여기서 복제한다
    // (findBundleUpgrade 는 순수 함수라 pool 을 남겨주지 않는다).
    const pool = new Map(basePool);
    pool.set(candidateSku, (pool.get(candidateSku) ?? 0) + candidate.quantity);

    // chosen 번들의 additionalQuantity만큼 풀에서 차감
    const resultItems: CartItem[] = [
      {
        sku: chosen.bundleSku,
        slug: chosen.bundle.slug,
        quantity: chosen.additionalQuantity,
        options: chosen.bundleOptions,
      },
    ];
    for (const sku of chosen.components) {
      pool.set(sku, (pool.get(sku) ?? 0) - chosen.additionalQuantity);
    }

    // 남은 풀에서 할인 큰 순서로 탐욕적 재번들링
    // ⚠️ 여기도 `?? 0` 금지, 그리고 위보다 더 위험하다 — 정렬 키가 discountPerUnit 이라
    // 번들가가 0 이 되면 할인폭이 최대로 잡혀 **가장 먼저** 적용된다. 반대로 구성품 가격이
    // 하나라도 없으면 componentTotal 이 과소 계상돼 discountPerUnit 이 음수가 되고,
    // 아래 `>= 0` 필터에 걸려 **정상 번들을 재구성하지 못한다**(분해된 채로 남아 개별가 청구).
    // 어느 쪽이든 가격이 불완전하면 할인 판단을 하지 않는 게 맞다.
    const allBundleOptions = bundleProducts.flatMap((bundle) =>
      (bundle.skuOptions ?? [])
        .filter(
          (opt) => opt.gameResells.length === 0 && opt.components.length > 0,
        )
        .map((opt) => {
          const bundlePrice = skuPriceMap.get(opt.sku);
          const componentPrices = opt.components.map((sku) =>
            skuPriceMap.get(sku),
          );
          // ⚠️ 이 `return null` 을 **지워도 지금은 테스트가 잡지 못한다**(실측: 순수 제거 시
          // 19 passed). `undefined` 가 아래 reduce 에서 NaN 을 만들고, 그 NaN 이
          // `discountPerUnit >= 0` 필터에 걸려 떨어지기 때문이다 — **우연한 2선 방어**다.
          // 그 필터를 `> 0` 으로 바꾸거나 `Number.isFinite` 처리를 넣거나 정렬을 손대면
          // masking 이 사라지고 이 가드가 유일한 방어가 된다. 필터를 바꿀 땐 여기도 같이 볼 것.
          // (테스트가 잡는 건 `?? 0` 으로 **대체**하는 변형이다 — #5112 · #5187)
          if (
            bundlePrice === undefined ||
            componentPrices.some((p) => p === undefined)
          ) {
            return null;
          }
          const componentTotal = (componentPrices as number[]).reduce(
            (sum, p) => sum + p,
            0,
          );
          return {
            bundleSlug: bundle.slug,
            bundleSku: opt.sku,
            bundleOptions: ProductUtil.getOptionsFromSkuOption(opt),
            components: opt.components,
            discountPerUnit: componentTotal - bundlePrice,
          };
        })
        .filter(
          (opt): opt is NonNullable<typeof opt> =>
            opt !== null && opt.discountPerUnit >= 0,
        ),
    );

    allBundleOptions.sort((a, b) => b.discountPerUnit - a.discountPerUnit);

    for (const opt of allBundleOptions) {
      const qtys = opt.components.map((sku) => pool.get(sku) ?? 0);
      if (!qtys.every((q) => q > 0)) continue;

      const qty = Math.min(...qtys, MAX_QUANTITY);
      if (qty <= 0) continue;

      const existing = resultItems.find((r) => r.sku === opt.bundleSku);
      if (existing) {
        const addQty = Math.min(qty, MAX_QUANTITY - existing.quantity);
        if (addQty <= 0) continue;
        existing.quantity += addQty;
        for (const sku of opt.components) {
          pool.set(sku, (pool.get(sku) ?? 0) - addQty);
        }
      } else {
        resultItems.push({
          sku: opt.bundleSku,
          slug: opt.bundleSlug,
          quantity: qty,
          options: opt.bundleOptions,
        });
        for (const sku of opt.components) {
          pool.set(sku, (pool.get(sku) ?? 0) - qty);
        }
      }
    }

    // 남은 개별 아이템 추가
    for (const item of expandedItems) {
      const remaining = pool.get(item.sku) ?? 0;
      if (remaining > 0) {
        resultItems.push({ ...item, quantity: remaining });
        pool.set(item.sku, 0);
      }
    }
    // candidate가 expandedItems에 없었던 경우 잔여 수량 추가
    if (!expandedItems.some((i) => i.sku === candidateSku)) {
      const remaining = pool.get(candidateSku) ?? 0;
      if (remaining > 0) {
        resultItems.push({
          sku: candidateSku,
          slug: candidate.slug,
          quantity: remaining,
          options: candidate.options ?? [],
        });
      }
    }

    return { items: resultItems, combinedResult: chosen.bundle.slug };
  }

  static makeGameBundles({
    bundles,
    deviceItems,
    gameItems,
  }: {
    bundles: Product[];
    deviceItems: CartItem[];
    gameItems: EssentialGameItem[];
  }): {
    bundles: CartItem[];
    remainingDeviceItems: CartItem[];
    remainingGameItems: EssentialGameItem[];
  } {
    const arizonaBundle = getArizonaBundle(bundles);
    let _deviceItems = [...deviceItems];
    let _gameItems = [...gameItems];
    const metroBundle = getMetroBundle(bundles);
    const result: CartItem[] = [];

    const applyBundle = (bundle: Product | undefined) => {
      if (!bundle) return;

      (bundle.skuOptions ?? []).forEach((option) => {
        if (_deviceItems.length === 0 || _gameItems.length === 0) {
          return;
        }

        const deviceIngredients: CartItem[] = [];
        const gameIngredients: (EssentialGameItem & { quantity: number })[] =
          [];

        option.components.forEach((sku) => {
          const ingredient = _deviceItems.find((item) => item.sku === sku);
          if (ingredient) {
            deviceIngredients.push(ingredient);
          }
        });

        option.gameResells.forEach((resell) => {
          const ingredients = _gameItems.filter(
            (item) =>
              item.gameResellId === resell.id &&
              item.platform === resell.platform,
          );
          if (ingredients.length > 0) {
            gameIngredients.push({
              ...ingredients[0],
              quantity: ingredients.length,
            });
          }
        });

        if (
          option.components.length === deviceIngredients.length &&
          option.gameResells.length === gameIngredients.length
        ) {
          const min = Math.min(
            ...deviceIngredients.map((item) => item.quantity),
            ...gameIngredients.map((item) => item.quantity),
          );

          _deviceItems = produce(_deviceItems, (draft) => {
            deviceIngredients.forEach((ingredient) => {
              const index = draft.findIndex(
                (item) => item.sku === ingredient.sku,
              );
              if (index !== -1) {
                draft[index].quantity -= min;
                if (draft[index].quantity <= 0) {
                  draft.splice(index, 1);
                }
              }
            });
          });

          _gameItems = produce(_gameItems, (draft) => {
            gameIngredients.forEach((ingredient) => {
              for (let i = 0; i < min; i++) {
                const index = draft.findIndex(
                  (item) =>
                    item.gameResellId === ingredient.gameResellId &&
                    item.platform === ingredient.platform,
                );
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              }
            });
          });

          const options =
            bundle.slug === ProductSlug.ARIZONA_BUNDLE
              ? option.options.map((value) => ({ type: "color", value }))
              : [];

          result.push({
            sku: option.sku,
            slug: bundle.slug,
            options,
            quantity: min,
          });
        }
      });
    };

    applyBundle(arizonaBundle);
    applyBundle(metroBundle);
    return {
      bundles: result,
      remainingDeviceItems: _deviceItems,
      remainingGameItems: _gameItems,
    };
  }
}

// 메트로 번들을 찾는 함수
// 뱃지 구성 요소는 메트로 번들의 증정품이므로 제외된 SKU 옵션으로 반환합니다.
const getMetroBundle = (products: Product[]) => {
  const product = products.find((p) => p.slug === ProductSlug.METRO_BUNDLE);
  if (!product) {
    return product;
  }
  return removeBadgeComponentsFromBundle(product);
};

const getArizonaBundle = (products: Product[]) => {
  const product = products.find((p) => p.slug === ProductSlug.ARIZONA_BUNDLE);
  if (!product) {
    return product;
  }
  return removeBadgeComponentsFromBundle(product);
};

const removeBadgeComponentsFromBundle = (product: Product) => {
  return {
    ...product,
    skuOptions: (product.skuOptions ?? []).map((o) => ({
      ...o,
      components: o.components.filter(
        (c) => !c.toLowerCase().includes("badge"),
      ),
    })),
  };
};
